"use client";

import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import type { EventParams } from "@/utils/analytics";
import { trackEvent } from "@/utils/analytics";
import { getOrCreateHomeEntryAttribution } from "@/utils/attribution";
import { cn } from "@/utils/cn";
import { getOrCreateHomeSessionId } from "@/utils/homeSession";
import { trackTrialFunnelEvent } from "@/utils/trialFunnelTracking";
import { getOrCreateTrialSessionId } from "@/utils/trialTracking";
import { getTrialBaseUrl } from "@/utils/trialUrl";

import { CompetencyStep } from "./trial-quickstart/CompetencyStep";
import { ConditionStep } from "./trial-quickstart/ConditionStep";
import { HomeWowPreview } from "./trial-quickstart/HomeWowPreview";
import { JdSearchModal } from "./trial-quickstart/JdSearchModal";
import type { LevelId, PositionId, Selection, StageId, WizardStep } from "./trial-quickstart/model";
import { freshSelection, getPosition, summarizeSelection, synthesizeJd } from "./trial-quickstart/model";
import { PositionStep } from "./trial-quickstart/PositionStep";
import { StepDots } from "./trial-quickstart/StepDots";
import {
  selectPosition,
  toggleCompetencySelection,
  toggleDomainSelection,
  toggleExcludeSelection,
  toggleRequiredSelection,
} from "./trial-quickstart/selection";

const TRIAL_EXTRACT_PATH = "/extract";

type TrialHeroVariantProps = {
  experimentParams: EventParams;
};

function byteLength(value: string) {
  return new TextEncoder().encode(value).length;
}

function buildLegacyJdDestination(baseUrl: string, method: "url" | "text", value: string, params: URLSearchParams) {
  const legacyParams = new URLSearchParams(params);
  legacyParams.delete("entry");
  legacyParams.delete("method");
  legacyParams.set(method === "url" ? "jdUrl" : "jd", value);
  const query = legacyParams.toString();
  return {
    query,
    url: `${baseUrl}${TRIAL_EXTRACT_PATH}?${query}`,
    encodedValueLength: encodeURIComponent(value).length,
  };
}

export default function TrialHeroVariant({ experimentParams }: TrialHeroVariantProps) {
  const path = usePathname() ?? "/";
  const [step, setStep] = useState<WizardStep>(1);
  const [selection, setSelection] = useState<Selection>(() => freshSelection("backend"));
  const [submitting, setSubmitting] = useState(false);
  const [jdModalOpen, setJdModalOpen] = useState(false);
  const viewedRef = useRef(false);
  const engagedRef = useRef(false);

  const attributionContext = useMemo(() => getOrCreateHomeEntryAttribution(), []);
  const position = getPosition(selection.positionId);
  const canProceed = step !== 2 || selection.competencyIds.length > 0;

  function selectionPayload(nextSelection: Selection = selection) {
    return {
      position: nextSelection.positionId,
      level: nextSelection.level,
      stage: nextSelection.stage,
      competencies: nextSelection.competencyIds,
      required: nextSelection.requiredIds,
      domains: nextSelection.domainIds,
      excludes: nextSelection.excludeIds,
      competency_count: nextSelection.competencyIds.length,
      required_count: nextSelection.requiredIds.length,
      domain_count: nextSelection.domainIds.length,
      exclude_count: nextSelection.excludeIds.length,
    };
  }

  function trackTrial(type: string, params: Record<string, unknown> = {}, nextSelection: Selection = selection) {
    return trackTrialFunnelEvent(type, {
      ...attributionContext,
      ...experimentParams,
      onboarding_version: "v2_quickstart",
      surface: "home",
      ...selectionPayload(nextSelection),
      ...params,
    });
  }

  function trackHome(eventName: string, params?: EventParams) {
    trackEvent(eventName, path, {
      ...attributionContext,
      ...experimentParams,
      onboarding_version: "v2_quickstart",
      surface: "home",
      ...params,
    });
  }

  function markEngaged() {
    if (engagedRef.current) return;
    engagedRef.current = true;
    const payload = {
      source: "inline",
      step,
      position: selection.positionId,
      level: selection.level,
    };
    trackHome("quickstart_opened", payload);
    trackTrial("quickstart_opened", payload);
  }

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackHome("quickstart_viewed", { step: 1 });
    trackTrial("quickstart_viewed", { step: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickPosition(id: PositionId) {
    markEngaged();
    const previousPosition = selection.positionId;
    const changed = previousPosition !== id;
    const nextSelection = selectPosition(selection, id);
    setSelection(() => nextSelection);
    const payload = {
      position: id,
      previous_position: previousPosition,
      changed,
      level: nextSelection.level,
      step: 1,
    };
    trackHome("quickstart_position_selected", payload);
    trackTrial("quickstart_position_selected", payload, nextSelection);
  }

  function pickLevel(id: LevelId) {
    markEngaged();
    const previousLevel = selection.level;
    const nextSelection = { ...selection, level: id };
    setSelection(() => nextSelection);
    const payload = {
      level: id,
      previous_level: previousLevel,
      changed: previousLevel !== id,
      position: selection.positionId,
      step: 1,
    };
    trackHome("quickstart_level_selected", payload);
    trackTrial("quickstart_level_selected", payload, nextSelection);
  }

  function toggleCompetency(id: string) {
    const result = toggleCompetencySelection(selection, id);

    setSelection(result.nextSelection);
    const payload = {
      position: selection.positionId,
      competency: id,
      selected: result.selected,
      selected_count_after: result.selectedCountAfter,
      required_count_after: result.requiredCountAfter,
      step: 2,
    };
    trackHome("quickstart_competency_toggled", payload);
    trackTrial("quickstart_competency_toggled", payload, result.nextSelection);
  }

  function toggleRequired(id: string) {
    const result = toggleRequiredSelection(selection, id);
    if (!result) return;

    setSelection(result.nextSelection);
    const payload = {
      position: selection.positionId,
      competency: id,
      required: result.required,
      selected_count: result.selectedCount,
      required_count_after: result.requiredCountAfter,
      step: 2,
    };
    trackHome("quickstart_required_toggled", payload);
    trackTrial("quickstart_required_toggled", payload, result.nextSelection);
  }

  function setStage(id: StageId) {
    const previousStage = selection.stage;
    const nextSelection = { ...selection, stage: id };
    setSelection(() => nextSelection);
    const payload = {
      stage: id,
      previous_stage: previousStage,
      changed: previousStage !== id,
      position: selection.positionId,
      level: selection.level,
      step: 3,
    };
    trackHome("quickstart_stage_selected", payload);
    trackTrial("quickstart_stage_selected", payload, nextSelection);
  }

  function toggleDomain(id: string) {
    const result = toggleDomainSelection(selection, id);
    if (!result) return;

    setSelection(result.nextSelection);
    const payload = {
      domain: id,
      selected: result.selected,
      domain_count_after: result.domainCountAfter,
      position: selection.positionId,
      level: selection.level,
      step: 3,
    };
    trackHome("quickstart_domain_toggled", payload);
    trackTrial("quickstart_domain_toggled", payload, result.nextSelection);
  }

  function toggleExclude(id: string) {
    const result = toggleExcludeSelection(selection, id);

    setSelection(result.nextSelection);
    const payload = {
      exclude: id,
      selected: result.selected,
      exclude_count_after: result.excludeCountAfter,
      position: selection.positionId,
      level: selection.level,
      step: 3,
    };
    trackHome("quickstart_exclude_toggled", payload);
    trackTrial("quickstart_exclude_toggled", payload, result.nextSelection);
  }

  function goTo(next: WizardStep) {
    markEngaged();
    const payload = {
      from_step: step,
      to_step: next,
      direction: next > step ? "next" : "prev",
      position: selection.positionId,
      level: selection.level,
    };
    trackHome("quickstart_step_changed", payload);
    trackTrial("quickstart_step_changed", payload);
    setStep(next);
  }

  async function finishQuickstart() {
    if (submitting) return;
    setSubmitting(true);
    const jd = synthesizeJd(selection);
    const trialSessionId = getOrCreateTrialSessionId();
    const homeSessionId = getOrCreateHomeSessionId();
    const params = new URLSearchParams({
      utm_source: "homepage",
      utm_medium: "organic",
      utm_campaign: "hero-quickstart",
      utm_content: "trial",
      quickstart: "1",
      sid: trialSessionId,
      hid: homeSessionId,
      experimentId: String(experimentParams.experiment_id || ""),
      variant: String(experimentParams.variant || ""),
      position: selection.positionId,
      level: selection.level,
      competencies: selection.competencyIds.join(","),
      required: selection.requiredIds.join(","),
      stage: selection.stage,
      domains: selection.domainIds.join(","),
      excludes: selection.excludeIds.join(","),
    });
    const destination = `${getTrialBaseUrl()}${TRIAL_EXTRACT_PATH}?${params.toString()}`;
    const summary = summarizeSelection(selection);

    trackHome("quickstart_completed", summary);
    trackTrial("quickstart_completed", { ...summary, step: 3 }, selection);
    const jdPayload = {
      method: "quickstart",
      position: selection.positionId,
      level: selection.level,
      length: jd.length,
      home_session_id: homeSessionId,
      trial_session_id: trialSessionId,
    };
    trackHome("jd_submitted", jdPayload);
    await trackTrial("jd_submitted", { ...jdPayload, jdText: jd }, selection);
    const redirectPayload = {
      method: "quickstart",
      position: selection.positionId,
      level: selection.level,
      home_session_id: homeSessionId,
      trial_session_id: trialSessionId,
      destination_host: new URL(destination).host,
      destination_path: TRIAL_EXTRACT_PATH,
    };
    trackHome("trial_redirect_started", redirectPayload);
    trackTrial("trial_redirect_started", redirectPayload, selection);

    window.location.href = destination;
  }

  function openJdSearch() {
    markEngaged();
    const payload = {
      source: "quickstart_inline",
      current_step: step,
      position: selection.positionId,
      level: selection.level,
    };
    trackHome("jd_search_opened", payload);
    trackTrial("jd_search_opened", payload);
    setJdModalOpen(true);
  }

  async function submitJdSearch({ value, method }: { value: string; method: "url" | "text" }) {
    if (submitting) return;
    setSubmitting(true);

    const trialSessionId = getOrCreateTrialSessionId();
    const homeSessionId = getOrCreateHomeSessionId();
    const params = new URLSearchParams({
      utm_source: "homepage",
      utm_medium: "organic",
      utm_campaign: "hero-jd-search",
      utm_content: "trial",
      entry: "stored",
      method,
      sid: trialSessionId,
      hid: homeSessionId,
      experimentId: String(experimentParams.experiment_id || ""),
      variant: String(experimentParams.variant || ""),
    });

    const trialBaseUrl = getTrialBaseUrl();
    const query = params.toString();
    const destination = `${trialBaseUrl}${TRIAL_EXTRACT_PATH}?${query}`;
    const legacyDestination = buildLegacyJdDestination(trialBaseUrl, method, value, params);

    const lengthPayload = {
      method,
      input_chars: value.length,
      input_utf8_bytes: byteLength(value),
      encoded_input_chars: legacyDestination.encodedValueLength,
      legacy_query_chars: legacyDestination.query.length,
      legacy_url_chars: legacyDestination.url.length,
      guarded_query_chars: query.length,
      guarded_url_chars: destination.length,
      legacy_over_8kb: legacyDestination.url.length > 8_000,
      legacy_over_16kb: legacyDestination.url.length > 16_000,
      home_session_id: homeSessionId,
      trial_session_id: trialSessionId,
    };
    trackHome("uri_length_guarded", lengthPayload);
    await trackTrial("uri_length_guarded", lengthPayload);

    const jdPayload = {
      method,
      length: value.length,
      home_session_id: homeSessionId,
      trial_session_id: trialSessionId,
    };
    trackHome("jd_submitted", jdPayload);
    await trackTrial("jd_submitted", { ...jdPayload, jdText: value });
    const redirectPayload = {
      method: `jd_${method}`,
      home_session_id: homeSessionId,
      trial_session_id: trialSessionId,
      destination_host: new URL(destination).host,
      destination_path: TRIAL_EXTRACT_PATH,
    };
    trackHome("trial_redirect_started", redirectPayload);
    trackTrial("trial_redirect_started", redirectPayload);

    window.location.href = destination;
  }

  return (
    <section className="trial-surface flex h-auto min-h-[620px] w-full flex-col items-center justify-center px-side py-6 desktop:min-h-[calc(100vh-4rem)] desktop:py-4">
      <div className="w-full max-w-5xl">
        <div className="mb-5 text-center tablet:mb-6">
          <div className="mb-2.5 flex justify-center tablet:mb-3">
            <span className="trial-glass-pill inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--trial-brand)]">
              <span className="trial-brand-gradient inline-flex h-4 w-4 items-center justify-center rounded-full text-white">
                ⌕
              </span>
              데이터로 검증하는 AI 리크루터
            </span>
          </div>
          <h1 className="text-[32px] font-bold leading-[1.08] tracking-[-0.025em] text-[var(--trial-foreground)] tablet:text-[44px]">
            공고로는 놓치던 인재를
            <br />
            <span className="trial-brand-gradient-text">RAG 기반 AI</span>가 발굴하고
            <br />
            데이터로 검증합니다
          </h1>
        </div>

        <div className="grid items-start gap-8 desktop:grid-cols-2 desktop:gap-12">
          <div>
            <div className="trial-glass-strong rounded-[28px] p-5 sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(79,109,245,0.1)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--trial-brand)]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--trial-brand)]" />
                  빠른 시작 · 약 30초
                </span>
                <StepDots step={step} />
              </div>

              {step === 1 ? <PositionStep selection={selection} onPickPosition={pickPosition} onPickLevel={pickLevel} /> : null}
              {step === 2 ? (
                <CompetencyStep
                  position={position}
                  selection={selection}
                  onToggle={toggleCompetency}
                  onToggleRequired={toggleRequired}
                />
              ) : null}
              {step === 3 ? (
                <ConditionStep
                  selection={selection}
                  onStage={setStage}
                  onToggleDomain={toggleDomain}
                  onToggleExclude={toggleExclude}
                />
              ) : null}

              <div className="mt-5 flex items-center justify-between gap-3 border-t border-[rgba(15,23,42,0.08)] pt-4">
                <button
                  type="button"
                  onClick={() => goTo((step - 1) as WizardStep)}
                  className={cn(
                    "px-2 py-2 text-[13.5px] text-[var(--trial-muted-strong)] transition-colors hover:text-[var(--trial-foreground)]",
                    step === 1 && "invisible",
                  )}
                >
                  ← 이전
                </button>
                <div className="flex items-center gap-2 tablet:gap-3">
                  <button
                    type="button"
                    onClick={openJdSearch}
                    className="inline-flex items-center gap-1 whitespace-nowrap px-1 py-2 text-[12.5px] text-[var(--trial-muted-strong)] transition-colors hover:text-[var(--trial-brand)]"
                    title="이미 정해둔 JD가 있으세요? JD로 바로 검색"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    JD로 검색
                  </button>
                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={() => goTo((step + 1) as WizardStep)}
                      disabled={!canProceed}
                      className="trial-brand-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_8px_24px_-6px_rgba(79,109,245,0.5)] transition-all enabled:hover:scale-[1.02] enabled:active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      다음 →
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={finishQuickstart}
                      disabled={submitting}
                      className="trial-brand-gradient inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-[14px] font-semibold text-white shadow-[0_8px_24px_-6px_rgba(79,109,245,0.5)] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                    >
                      {submitting ? "이동 중..." : "3명 후보 발굴 시작 →"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-2 flex items-center justify-center gap-1.5 text-[12px] text-[var(--trial-muted)]">
              <span className="text-[var(--trial-success)]">✓</span>
              가입 없이 · 무료
            </div>
          </div>

          <HomeWowPreview />
        </div>
      </div>
      <JdSearchModal open={jdModalOpen} onClose={() => setJdModalOpen(false)} onSubmit={submitJdSearch} onTrack={trackHome} />
    </section>
  );
}
